import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

 
function cascadeRemovePath(state, pathId) {
  const targetPath = state.paths.find((p) => p.id === pathId);

  const deletedNodeIds = targetPath
    ? new Set(targetPath.points.map((pt) => pt.id))
    : new Set();

  const toDeleteIds = state.paths
    .filter((p) => {
      if (p.id === pathId) return false;
      if (p.points.some((pt) => pt.snapPathId === pathId)) return true;
      if (deletedNodeIds.size > 0) {
        const first = p.points[0]?.id;
        const last  = p.points[p.points.length - 1]?.id;
        if (first && deletedNodeIds.has(first)) return true;
        if (last  && deletedNodeIds.has(last))  return true;
      }
      return false;
    })
    .map((p) => p.id);

  state.paths = state.paths.filter((p) => p.id !== pathId);

  if (state.selectedPathId === pathId) {
    state.selectedPathId  = null;
    state.selectedPointId = null;
  }
  state.eraserSelectedPoints = state.eraserSelectedPoints.filter(
    (ep) => ep.pathId !== pathId,
  );

  for (const sid of toDeleteIds) {
    cascadeRemovePath(state, sid);
  }
}

const initialState = {
  paths:                [],
  activeTool:           null,
  activePath:           'main',
  selectedPathId:       null,
  selectedPointId:      null,
  eraserSelectedPoints: [],  
  inProgress:           null,
  shortestPath:         null,
  saveStatus:           'idle',
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: { 
    setNavActiveTool(state, { payload }) {
      state.activeTool           = payload;
      state.inProgress           = null;
      state.selectedPathId       = null;
      state.selectedPointId      = null;
      state.eraserSelectedPoints = [];
    },
    setNavActivePath(state, { payload }) {
      state.activePath = payload;
      state.inProgress = null;
    },
 
    setInProgress(state, { payload }) {
      state.inProgress = payload;
    },
    clearInProgress(state) {
      state.inProgress = null;
    },
 
    addPath(state, { payload }) {  
      state.paths.push({ id: uuid(), ...payload });
      state.inProgress = null;
    },
 
    updatePoint(state, { payload: { pathId, pointId, position, snapT } }) {
      const path = state.paths.find((p) => p.id === pathId);
      if (!path) return; 
      for (const pt of path.points) {
        if (pt.id !== pointId) continue;
        pt.position = position;
        if (snapT !== undefined) pt.snapT = snapT;
      }
    },
 
    updatePathPoints(state, { payload: { pathId, points } }) {
      const path = state.paths.find((p) => p.id === pathId);
      if (path) path.points = points;
    },
 
    updateSnapPoint(state, { payload: { pathId, pointId, position, snapT, snapPathId } }) {
      const path = state.paths.find((p) => p.id === pathId);
      if (!path) return;
      for (const pt of path.points) {
        if (pt.id !== pointId) continue;
        pt.position = position;
        pt.snapT    = snapT;
        if (snapPathId !== undefined) pt.snapPathId = snapPathId;
      }
    },
 
    removePath(state, { payload: pathId }) {
      cascadeRemovePath(state, pathId);
    },

    removePathOnly(state, { payload: pathId }) {
      state.paths = state.paths.filter((p) => p.id !== pathId);
      if (state.selectedPathId === pathId) {
        state.selectedPathId  = null;
        state.selectedPointId = null;
      }
      state.eraserSelectedPoints = state.eraserSelectedPoints.filter(
        (ep) => ep.pathId !== pathId,
      );
    },

    removePointFromPath(state, { payload: { pathId, pointId } }) {
      const path = state.paths.find((p) => p.id === pathId);
      if (!path) return;
      path.points = path.points.filter((pt) => pt.id !== pointId);
      if (path.points.length < 2) {
        state.paths = state.paths.filter((p) => p.id !== pathId);
        if (state.selectedPathId === pathId) {
          state.selectedPathId  = null;
          state.selectedPointId = null;
        }
        state.eraserSelectedPoints = state.eraserSelectedPoints.filter(
          (ep) => ep.pathId !== pathId,
        );
      }
    },
 
    selectPath(state, { payload: pathId }) {
      state.selectedPathId  = pathId;
      state.selectedPointId = null;
    },
    selectPoint(state, { payload: { pathId, pointId } }) {
      state.selectedPathId  = pathId;
      state.selectedPointId = pointId;
    },
    clearNavSelection(state) {
      state.selectedPathId  = null;
      state.selectedPointId = null;
    },

    setEraserSelectedPoints(state, { payload }) {
      state.eraserSelectedPoints = payload; 
    },
    clearEraserSelectedPoints(state) {
      state.eraserSelectedPoints = [];
    },
 
    bulkAddPaths(state, { payload: paths }) {
      state.paths.push(...paths);
    },
 
    insertPointAtIndex(state, { payload: { pathId, index, point } }) {
      const path = state.paths.find((p) => p.id === pathId);
      if (!path) return;
      path.points.splice(index, 0, point);
    },
 
    updatePinConnectedPoints(state, { payload: { pinId, position, floorId } }) {
      state.paths.forEach((path) => {
        if (floorId && path.floorId !== floorId) return;
        path.points.forEach((pt) => {
          if (pt.pinId === pinId) pt.position = position;
        });
      });
    },
 
    setAllPaths(state, { payload: paths }) {
      state.paths = paths;
    },
 
    clearAllNavPaths(state) {
      state.paths      = [];
      state.inProgress = null;
      state.selectedPathId  = null;
      state.selectedPointId = null;
    },

    resetNavigation() {
      return initialState;
    },
 
    clearFloorPaths(state, { payload: floorId }) {
      state.paths = state.paths.filter((p) => p.floorId !== floorId);
      state.eraserSelectedPoints = [];
      state.selectedPathId  = null;
      state.selectedPointId = null;
    },
 
    setShortestPath(state, { payload }) {
      state.shortestPath = payload;
    },
    clearShortestPath(state) {
      state.shortestPath = null;
    },
 
    setSaveStatus(state, { payload }) {
      state.saveStatus = payload;
    },
  },
});

export const {
  setNavActiveTool,
  setNavActivePath,
  setInProgress,
  clearInProgress,
  addPath,
  updatePoint,
  updatePathPoints,
  updateSnapPoint,
  removePath,
  removePathOnly,
  removePointFromPath,
  selectPath,
  selectPoint,
  clearNavSelection,
  setEraserSelectedPoints,
  clearEraserSelectedPoints,
  bulkAddPaths,
  insertPointAtIndex,
  updatePinConnectedPoints,
  setAllPaths,
  clearAllNavPaths,
  resetNavigation,
  clearFloorPaths,
  setShortestPath,
  clearShortestPath,
  setSaveStatus,
} = navigationSlice.actions;

export default navigationSlice.reducer;
